import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth, Roles } from "../../providers/AuthContext";
import { postCreateParty } from "../../api/RemotePartiesOCPI"; // Import API function
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import PartyForm from "../../components/ocpiParty/PartyForm";
import { useCallback } from "react";
import { useEffect } from "react";
import { getAllCustomers } from "../../api/Customers";
import { getCustomerDisplayName } from "../../helpers/CustomerDisplay";

const CreateOcpiPartyPage = () => {
    const { t } = useTranslation();
    const { getAccessToken, hasRole } = useAuth(); // Get the access token from context

    const navigate = useNavigate();
    const [loading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [invalidFields, setInvalidFields] = useState([]);


    // Define initial state for the form
    const [party, setParty] = useState({
        name: "",
        remotePartyName: "",
        RemotePartyPublicId: "",
        tokenA: "",
        customerName: "",
        isOcpiHandshakeCompleted: false,
        customerPublicId: ""
    });

    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        const handleGetCustomers = async () => {
            try {
                const accessToken = await getAccessToken();
                const newCustomers = await getAllCustomers(accessToken);
                setCustomers(
                    (newCustomers || []).map((customer) => ({
                        ...customer,
                        displayName: getCustomerDisplayName(customer, hasRole(Roles.Admin)),
                    }))
                );

            } catch (error) {
                console.error(error);
            }
        };
        if (customers.length === 0) {
            handleGetCustomers();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, []);


    // Handler for form input changes
    const handleInputChange = useCallback((key, value) => {
        setHasChanges(true);
        setParty((prev) => ({ ...prev, [key]: value }));
    }, []);

    // Form submission handler
    const handleCreateOcpiParty = async () => {
        const missing = []
        const identifiers = party.Identifiers || [];
        if (
            (identifiers.includes("cdrs") ||
                identifiers.includes("sessions") ||
                identifiers.includes("booking")) &&
            (party.customerPublicId === "" || party.customerPublicId == null)
        ) {
            missing.push({ key: "customerPublicId", value: t("entity.customer") });
        }
        if (missing.length > 0) {
            setInvalidFields(missing.map(item => item.key));

            const message = t("common.pleaseFillRequiredFields", { fields: missing.map(obj => obj.value).join(", ") });

            toast.error(message);
            return;
        }
        setInvalidFields([]);
        const toastId = toast.loading(t("toast.creating", { entity: t("entity.ocpiParty") }));

        try {
            const accessToken = await getAccessToken();
            if (!party.startDateTimeUtc) {
                party.startDateTimeUtc = new Date().toISOString();
            }
            const newParty = await postCreateParty(party, accessToken); // Make the API call to create a site


            toast.success(t("toast.created", { entity: t("entity.ocpiParty") }), { id: toastId });

            navigate(`/ocpi-party/${newParty.remotePartyPublicId}`);

        } catch (error) {
            toast.error(error?.message || t("toast.failedCreate", { entity: t("entity.ocpiParty") }), {
                id: toastId,
            });
        }
    };

    const navigateBack = () => {
        if (navigate.length > 1) {
            navigate(-1);
        } else {
            navigate("/ocpi-parties");
        }
    }

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={t("nav.ocpiParties")} loading={loading} />
                    </div>

                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button
                                onClick={() => navigateBack()}
                                className="text-black flex"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>
                            <h2 className="border-l border-mute-200 pl-4 ">{t("pages.createOcpiParty")}</h2>
                            <div className="flex gap-4 ml-auto">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => navigateBack()}
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!party?.name && !hasChanges}
                                    onClick={handleCreateOcpiParty}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        {/* Charging site form */}
                        <div className=" px-4 py-8 shadow-lg rounded-md w-full">
                            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
                                <PartyForm
                                    party={party}
                                    handleInputChange={handleInputChange}
                                    customers={customers}
                                    invalidFields={invalidFields}
                                />
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default CreateOcpiPartyPage;
